import { Typography } from '@capsule/consumer-design-system';

import { format, parseISO } from 'date-fns';
import { useHistory, useParams } from 'react-router';
import { css } from 'styled-components';

import { useFetchRescheduleDeliveryWindows } from 'src/app/client/models/rescheduleDeliveryWindows';
import { Card } from 'src/app/components/Elements/Card';
import StandardPage from 'src/app/components/Elements/Page/Standard';
import Spinner from 'src/app/components/Elements/Spinner';
import { useGetOrder, useOrderPatch } from 'src/app/hooks/data/order';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { orderTracking } from 'src/app/router/paths';
import { getPreviousPathName } from 'src/app/state/selectors';
import { useSelector } from 'src/app/state/store';
import { formatDeliveryWindowTimeRange } from 'src/app/utils';

import { DeliveryWindowItem } from '../components/DeliveryWindowItem';
import { EmptyWindowDay } from '../components/EmptyWindowDay';
import { NoAvailableDeliveryWindowsCard } from '../components/NoAvailableDeliveryWindowsCard';
import { DateGroupContainer } from '../components/styles';

import { useRescheduleAnalytics } from './useRescheduleAnalytics';

const RESCHEDULE = 'Reschedule';
export const RescheduleDeliveryWindow = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const { push } = useHistory();

  const previousPathName = useSelector(getPreviousPathName);

  const { data: order, isLoading: isLoadingOrder, isError: isGetOrderError } = useGetOrder(orderId);

  const {
    mutateAsync: patchOrderAsync,
    isLoading: isPatchingOrder,
    isError: isPatchOrderError,
  } = useOrderPatch(orderId);

  const {
    rescheduleDeliveryWindows,
    isLoading: isFetchWindowsLoading,
    hasError: hasDeliveryWindowsError,
  } = useFetchRescheduleDeliveryWindows(orderId);

  const trackEventSubmitDeliveryReschedule = useSegmentEvent('submitDeliveryReschedule', { shouldTrackOnMount: false });
  const trackEventDeliveryRescheduleSucccess = useSegmentEvent('deliveryRescheduleSuccessful', {
    shouldTrackOnMount: false,
  });
  const trackEventDeliveryRescheduleFailure = useSegmentEvent('deliveryRescheduleFailed', {
    shouldTrackOnMount: false,
  });
  const trackEventSelectRescheduleDeliveryWindow = useSegmentEvent('selectRescheduleDeliveryWindow', {
    shouldTrackOnMount: false,
  });

  useRescheduleAnalytics(orderId, !isFetchWindowsLoading, rescheduleDeliveryWindows);

  if (isFetchWindowsLoading || isLoadingOrder || isPatchingOrder) {
    return (
      <StandardPage title={RESCHEDULE}>
        <Spinner centered />
      </StandardPage>
    );
  }

  return (
    <StandardPage title={RESCHEDULE}>
      {hasDeliveryWindowsError ||
      isPatchOrderError ||
      isGetOrderError ||
      (!hasDeliveryWindowsError && !rescheduleDeliveryWindows) ? (
        <NoAvailableDeliveryWindowsCard />
      ) : (
        rescheduleDeliveryWindows?.map(({ day, deliveryWindows }) => {
          return (
            <DateGroupContainer key={day}>
              <Typography
                css={css`
                  color: ${({ theme }) => theme.colors.capsuleBlue50};
                `}
                variant="overline"
              >
                {day}
              </Typography>
              <Card>
                <>
                  {deliveryWindows?.length === 0 ? (
                    <EmptyWindowDay text={'NO AVAILABLE TIMES'} />
                  ) : (
                    deliveryWindows.map((deliveryWindow, index) => {
                      const nextDeliveryWindow = deliveryWindows[index + 1];

                      return (
                        <DeliveryWindowItem
                          key={`window_${deliveryWindow.id}`}
                          deliveryWindow={deliveryWindow}
                          nextWindow={nextDeliveryWindow}
                          activeDeliveryWindowId={null}
                          handleClick={async deliveryWindow => {
                            const formattedDeliveryWindow = deliveryWindow
                              ? formatDeliveryWindowTimeRange(
                                  deliveryWindow?.startDate,
                                  deliveryWindow?.endDate,
                                  deliveryWindow?.category,
                                  deliveryWindow?.facilityTimezone
                                )
                              : 'No Available Windows';

                            trackEventSelectRescheduleDeliveryWindow({
                              source: previousPathName || null,
                              order_id: orderId,
                              selected_date: format(parseISO(deliveryWindow?.startDate), 'MM/dd/yyyy'),
                              selected_window: formattedDeliveryWindow,
                            });

                            trackEventSubmitDeliveryReschedule({
                              order_id: orderId,
                              delivery_address_id: order?.deliveryAddress.id ?? '',
                            });

                            await patchOrderAsync(
                              { deliveryWindowUUID: deliveryWindow.id },
                              {
                                onSuccess: () => {
                                  trackEventDeliveryRescheduleSucccess({ order_id: orderId });
                                  push(orderTracking.toPath({ orderId }));
                                },
                                onError: error => {
                                  trackEventDeliveryRescheduleFailure({
                                    order_id: orderId,
                                    error_code: error?.code ?? 'Unknown',
                                    error_summary: error?.message,
                                  });
                                },
                              }
                            );
                          }}
                        />
                      );
                    })
                  )}
                </>
              </Card>
            </DateGroupContainer>
          );
        })
      )}
    </StandardPage>
  );
};
